import React, { useState } from 'react';
import { Box, Typography, Tooltip, Chip, IconButton, Grow } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodiCodeBlock from './CodiCodeBlock';
import { generateCitationUrl, getCitationIcon } from './CodiChatUtils';
import MarkdownViewerDialog from './MarkdownViewerDialog';

const CodiMessageBubble = ({
  message,
  index,
  isClassic,
  streaming,
  formatTimestamp,
  onRegenerate,
  onRate,
  onNavigate,
  onCloseDialog
}) => {
  const isUser = message.role === 'user';

  // State for markdown viewer dialog
  const [markdownDialogOpen, setMarkdownDialogOpen] = useState(false);
  const [selectedMarkdownPath, setSelectedMarkdownPath] = useState(null);
  const [selectedMarkdownTitle, setSelectedMarkdownTitle] = useState('');

  return (
    <Grow
      in={true}
      timeout={600}
      style={{
        transformOrigin: isUser ? 'right center' : 'left center',
        transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 2 }}>
        <Box
          sx={(theme) => ({
            maxWidth: '85%',
            bgcolor: isUser
              ? 'primary.main'
              : (isClassic
                  ? 'grey.100'
                  : (theme.palette.mode === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.9)')),
            color: isUser ? 'white' : 'text.primary',
            borderRadius: isClassic ? '4px' : '20px',
            p: 2.5,
            boxShadow: isUser
              ? '0 4px 20px rgba(74, 144, 226, 0.3)'
              : (isClassic ? 1 : '0 4px 16px rgba(0, 0, 0, 0.08)'),
            border: isUser ? 'none' : (isClassic ? '1px solid' : 'none'),
            borderColor: isUser ? 'transparent' : 'divider',
            backdropFilter: isUser ? 'none' : 'blur(10px)',
            transition: 'all 0.2s ease-in-out'
          })}
        >
          {isUser ? (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {message.content}
            </Typography>
          ) : (
            <Box
              sx={(theme) => ({
                '& p': { margin: 0, marginBottom: 1 },
                '& p:last-child': { marginBottom: 0 },
                '& code': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : (isClassic ? 'grey.200' : 'grey.100'),
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.87)'
                    : 'inherit',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em'
                },
                '& pre': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? '#2C2C2C'
                    : (isClassic ? 'grey.200' : 'grey.100'),
                  p: 1,
                  borderRadius: '8px',
                  overflow: 'auto',
                  '& code': { bgcolor: 'transparent', px: 0, py: 0 }
                },
                '& ul, & ol': { marginTop: 0.5, marginBottom: 0.5, paddingLeft: 2 },
                '& li': { marginBottom: 0.25 },
                '& table': { width: '100%', borderCollapse: 'collapse', marginTop: 1, marginBottom: 1, fontSize: '0.9em' },
                '& th': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : (isClassic ? 'grey.200' : 'grey.100'),
                  padding: 1,
                  textAlign: 'left',
                  borderBottom: '2px solid',
                  borderColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'divider',
                  fontWeight: 600
                },
                '& td': {
                  padding: 1,
                  borderBottom: '1px solid',
                  borderColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'divider'
                },
                '& tr:last-child td': { borderBottom: 'none' }
              })}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ code: CodiCodeBlock }}>
                {message.content}
              </ReactMarkdown>
            </Box>
          )}

          {message.complete && message.citations && message.citations.length > 0 && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 0.75, fontWeight: 600, color: isUser ? 'rgba(255,255,255,0.9)' : 'text.secondary' }}
              >
                Quellen:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {message.citations.map((citation, idx) => {
                  const url = generateCitationUrl(citation);
                  const icon = getCitationIcon(citation.source_type);
                  const label = citation.title || citation.source || citation.path || citation.source_type;

                  const handleCitationClick = (e) => {
                    // Check if this is a doc/100-user-hilfen markdown file
                    if (citation.source_type === 'doc' && citation.path?.startsWith('doc/100-user-hilfen/')) {
                      e.preventDefault();
                      setSelectedMarkdownPath(citation.path);
                      setSelectedMarkdownTitle(citation.title || citation.path.split('/').pop().replace('.md', ''));
                      setMarkdownDialogOpen(true);
                      return;
                    }

                    // Handle regular navigation
                    if (!url) return;
                    if (url.startsWith('/')) {
                      e.preventDefault();
                      if (onNavigate) onNavigate(url);
                      if (onCloseDialog) onCloseDialog();
                    }
                  };

                  // Make chip clickable if it has a URL or is a doc/100-user-hilfen file
                  const isClickable = url || (citation.source_type === 'doc' && citation.path?.startsWith('doc/100-user-hilfen/'));

                  const chip = isClickable ? (
                    <Chip
                      icon={icon}
                      label={label}
                      onClick={handleCitationClick}
                      clickable
                      size="small"
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isUser ? 'rgba(255,255,255,0.2)' : 'action.hover',
                        '&:hover': { bgcolor: isUser ? 'rgba(255,255,255,0.3)' : 'action.selected' }
                      }}
                    />
                  ) : (
                    <Chip
                      icon={icon}
                      label={label}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: isUser ? 'rgba(255,255,255,0.3)' : 'divider', color: isUser ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}
                    />
                  );

                  const tooltipTitle = citation.title || citation.path || citation.source || citation.source_type;
                  return (
                    <Tooltip key={`tt-${idx}`} title={tooltipTitle} arrow>
                      {chip}
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Typography
              variant="caption"
              sx={{ opacity: 0.7, fontSize: '0.7rem', color: isUser ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}
            >
              {formatTimestamp(message.timestamp)}
            </Typography>

            {!isUser && message.complete && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Nachricht kopieren">
                  <IconButton
                    size="small"
                    onClick={() => {
                      (async () => {
                        try { await navigator.clipboard.writeText(message.content); }
                        catch {
                          try {
                            const ta = document.createElement('textarea');
                            ta.value = message.content || '';
                            ta.style.position = 'fixed'; ta.style.left = '-9999px';
                            document.body.appendChild(ta); ta.focus(); ta.select();
                            document.execCommand('copy'); document.body.removeChild(ta);
                          } catch {}
                        }
                      })();
                    }}
                    aria-label="Nachricht kopieren"
                    sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Antwort neu generieren">
                  <IconButton
                    size="small"
                    onClick={() => onRegenerate && onRegenerate(index)}
                    disabled={streaming}
                    aria-label="Antwort neu generieren"
                    sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <RefreshIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Daumen runter">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onRate && onRate(index, -1)}
                      disabled={streaming}
                      aria-label="Daumen runter"
                      sx={{ opacity: message.rating === -1 ? 1 : 0.6, '&:hover': { opacity: 1 } }}
                    >
                      <ThumbDownIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Daumen hoch">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onRate && onRate(index, 1)}
                      disabled={streaming}
                      aria-label="Daumen hoch"
                      sx={{ opacity: message.rating === 1 ? 1 : 0.6, '&:hover': { opacity: 1 } }}
                    >
                      <ThumbUpIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>

        {/* Markdown Viewer Dialog */}
        <MarkdownViewerDialog
          open={markdownDialogOpen}
          onClose={() => setMarkdownDialogOpen(false)}
          filePath={selectedMarkdownPath}
          title={selectedMarkdownTitle}
        />
      </Box>
    </Grow>
  );
};

export default CodiMessageBubble;
